import React, { useState } from 'react';
import { LogOut, X } from 'lucide-react';
import Button from '../../../ui/Button';

interface ExitCardProps {
  onClose?: () => void;
  onSave?: (exitData: ExitData) => void;
}

interface ExitData {
  exitCondition: string;
}

const ExitCard: React.FC<ExitCardProps> = ({ onClose, onSave }) => {
  const [exitData, setExitData] = useState<ExitData>({
    exitCondition: 'stop_matching_trigger',
  });

  const handleSave = () => {
    onSave?.(exitData);
    onClose?.();
  };

  const exitOptions = [
    {
      value: 'conversion_criteria',
      label: 'They match the conversion criteria',
    },
    { value: 'stop_matching_trigger', label: 'They stop matching the trigger' },
    {
      value: 'conversion_or_trigger',
      label:
        'They match the conversion criteria or they stop matching the trigger',
    },
    {
      value: 'no_early_exit',
      label: "People don't exit early, they move through the entire workflow",
    },
  ];

  return (
    <div className={` block `}>
      <div className="bg-card w-[32vw] border-l-2 border-border shadow-sm rounded-r-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-success-background rounded-lg flex items-center justify-center">
              <LogOut className="w-4 h-4 text-success" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Exit</h2>
              <p className="text-xs text-muted-foreground">
                Set the conditions for when people exit the campaign.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Exit Section */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground text-sm">Exit</h3>
            <p className="text-xs text-muted-foreground">
              A person will exit the campaign early, when:
            </p>

            <div className="space-y-2">
              {exitOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="exitCondition"
                    value={option.value}
                    checked={exitData.exitCondition === option.value}
                    onChange={(e) =>
                      setExitData((prev) => ({
                        ...prev,
                        exitCondition: e.target.value,
                      }))
                    }
                    className="w-4 h-4 text-primary bg-muted border-border focus:ring-0"
                  />
                  <span className="text-sm text-foreground">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>

            <p className="text-xs text-muted-foreground mt-3">
              The exit conditions are checked before sending every message in a
              campaign.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            size="small"
            className="text-sm"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            size="small"
            className="text-sm"
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExitCard;
